import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import { FadeIn } from './FadeIn';
import { SectionHeader } from './SectionHeader';
import { PortfolioModal } from './PortfolioModal';
import { LivePreviewFrame } from './LivePreviewFrame';
import { AnimatedBackground } from './AnimatedBackground';
import { CategoryTiles } from './CategoryTiles';
import { portfolioSites } from '../data/portfolio';
import { scrollToForm } from '../utils/scroll';
import type { NicheId, PortfolioSite } from '../types';
export function Portfolio() {
  const [activeFilter, setActiveFilter] = useState<NicheId | 'all'>('all');
  const [activeSite, setActiveSite] = useState<PortfolioSite | null>(null);
  const visibleSites = useMemo(() => activeFilter === 'all' ? portfolioSites : portfolioSites.filter(site => site.niche === activeFilter), [activeFilter]);
  return <section id="portafolio" className="cv-auto relative w-full overflow-hidden bg-white px-6 py-24" data-id="element-231" data-mp="imu003fgh2u7">
      <AnimatedBackground intensity="light" icons data-id="element-232" data-mp="mpqebhxyqcsc" />
      <div className="relative mx-auto max-w-[1100px]" data-id="element-233" data-mp="8vfxrnw4doxd">
        <FadeIn data-id="element-234" data-mp="dvlsjcxorrjo">
          <SectionHeader badge="Portafolio" badgeIcon={<WorkOutlineIcon sx={{
          fontSize: 15
        }} data-id="element-236" data-mp="dthhmb1dds9w" />} title={<>
                Webs que hemos
                <br data-id="element-237" data-mp="3x62zeeb4qca" />
                creado para negocios.
              </>} subtitle='Haz click en "Ver en Vivo" para previsualizar cada sitio en tiempo real, tal como lo verá tu cliente.' data-id="element-235" data-mp="dv29tu07oz4l" />
        </FadeIn>

        <div className="mb-10" data-id="element-238" data-mp="3r2x1tsjvzxam">
          <CategoryTiles value={activeFilter} onChange={setActiveFilter} ariaLabel="Filtrar portafolio por tipo de negocio" data-id="element-239" data-mp="3r3ym8nmslw50" />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-240" data-mp="1p9za601ujar9">
          {visibleSites.map((site, index) => <motion.article key={site.id} layout initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: Math.min(index * 0.07, 0.35),
          ease: [0.16, 1, 0.3, 1]
        }} whileHover={{
          y: -8
        }} className="group overflow-hidden rounded-2xl border-2 border-transparent bg-white shadow-card transition-shadow duration-300 hover:border-brand-blue hover:shadow-glow-blue" data-id="element-241" data-mp="1iuiglx0uqfk1" data-mpi={index}>
              <button type="button" onClick={() => setActiveSite(site)} aria-label={`Ver vista previa de ${site.name}`} className="relative block h-[200px] w-full overflow-hidden bg-mist focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-blue" data-id="element-242" data-mp="1ws6x6snvz0ja">
                {site.live ? <>
                    <LivePreviewFrame url={site.url} title={`Vista previa de ${site.name}`} data-id="element-243" data-mp="2wu3lfjkspi0z" />
                    <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-brand-deep/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white" data-id="element-244" data-mp="31rlqgyvozd3p">
                      <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-brand-green" data-id="element-245" data-mp="3atbq2rzq9ick" />
                      Sitio en vivo
                    </span>
                  </> : <img src={site.image} alt={`Sitio web de ${site.name}`} loading="lazy" className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105" data-id="element-246" data-mp="3kk6dhzopmnzl" />}
              </button>

              <div className="px-5 py-4" data-id="element-247" data-mp="2bp0ysb83ckv">
                <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-brand-blue" data-id="element-248" data-mp="1024p58baf5sp">
                  {site.nicheLabel}
                </p>
                <h3 className="mb-1 font-display text-base font-bold text-ink" data-id="element-249" data-mp="v20iea06lpfo">
                  {site.name}
                </h3>
                <p className="mb-3 text-xs text-muted" data-id="element-250" data-mp="14t4nmi021vxb">{site.description}</p>
                <div className="flex gap-2" data-id="element-251" data-mp="h4275dh5q94e">
                  <button type="button" onClick={() => setActiveSite(site)} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-brand-blue/10 px-2 py-2 text-xs font-semibold text-brand-blue transition hover:bg-brand-blue hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue" data-id="element-252" data-mp="h4v4xoy51oae">
                    <DesktopWindowsOutlinedIcon sx={{
                  fontSize: 15
                }} data-id="element-253" data-mp="21ic7zmoxtyzn" />
                    Ver en Vivo
                  </button>
                  <button type="button" onClick={scrollToForm} className="flex-1 rounded-lg bg-brand-yellow px-2 py-2 text-xs font-semibold text-brand-deep transition hover:bg-brand-yellow-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue" data-id="element-254" data-mp="1wn6nmsb6osdo">
                    ¡Quiero una así!
                  </button>
                </div>
              </div>
            </motion.article>)}
        </div>
      </div>

      <PortfolioModal site={activeSite} onClose={() => setActiveSite(null)} onCta={() => {
      setActiveSite(null);
      window.setTimeout(scrollToForm, 250);
    }} data-id="element-255" data-mp="2srfdu7b3t0z9" />
    </section>;
}